import AvatarImage from "@dropDesk/presentation/components/avatar";
import HighLightWords from "@dropDesk/presentation/components/high_light_words";
import React, {useEffect, useRef} from "react";
import {UserRole} from "@dropDesk/domain/entities/user/user_enum";
import {useInjection} from "inversify-react";
import {UserController} from "@dropDesk/presentation/pages/user/controller/user.controller";
import {observer} from "mobx-react";
import {
    SelectSearchSearchUserType
} from "@dropDesk/presentation/components/inputs/select_search_users/select_search_user";
import SelectSearchRemote from "@dropDesk/presentation/components/inputs/select_search_remote";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";
import {Select} from "antd";
import {
    ContainerColumn,
    ContainerHeaderColumn,
    ContainerHeaderText,
    ContentColumn,
    TextMainEllipsis,
} from "@dropDesk/presentation/components/inputs/select_search_users/styled";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {setBlockUpdateLocalSearchCache} from "@dropDesk/utils/helpers/common";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";

const {Option} = Select;

const SelectSearchUser = observer((props: SelectSearchSearchUserType) => {


    const userController = useInjection(UserController);
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const prevIdClientRef = useRef<string | undefined>(undefined);
    const prevIdSectorRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const prev = prevIdClientRef.current;
        const next = props.idClient;

        if (next === undefined) {
            prevIdClientRef.current = next;
            return;
        }

        if (prev === next) return;

        prevIdClientRef.current = next;
        userController.tableUsers.page = 0;
        userController.tableUsers.data = [];
        userController.setRole(UserRole.userClient);
        userController.list(userController.searchParam, next, undefined, undefined);
    }, [props.idClient]);

    useEffect(() => {
        const prev = prevIdSectorRef.current;
        const next = props.idSector;

        if (next === undefined) {
            prevIdSectorRef.current = next;
            return;
        }

        if (prev === next) return;

        prevIdSectorRef.current = next;
        userController.tableUsers.page = 0;
        userController.tableUsers.data = [];
        userController.setRole(UserRole.attendant);
        userController.list(userController.searchParam, undefined, next, undefined);
    }, [props.idSector]);

    useEffect(() => {
        userController.tableUsers.limit = ConstantsKeys.defaultLimitSelectSearchItems;
        userController.setRole(props.role);
    }, []);


    const onSearch = (searchParam: string): void => {
        setBlockUpdateLocalSearchCache(true);
        userController.list(searchParam ?? userController.searchParam, props.idClient, props.idSector, undefined);
    }

    const itemWidth = 200;

    const options =
        props.role === UserRole.attendant ?
            <>
                {userController.tableUsers.data.map(user => (
                    <Option key={user.id} value={user.id} label={user.name} data={user}>
                        <ContainerColumn>
                            <ContentColumn width={itemWidth}>
                                <AvatarImage
                                    useCursorPointer={!!user.urlImageProfile}
                                    title={user.name}
                                    size={25}
                                    url={user.urlImageProfile ?? ''}
                                    round={true}
                                    withBorder={true}
                                    hasPreviewImage={false}
                                />
                                <TextMainEllipsis style={{marginLeft: 5}}>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.name}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>
                            <ContentColumn width={itemWidth}>
                                <TextMainEllipsis>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.email ?? ''}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>
                            <ContentColumn width={itemWidth}>
                                <TextMainEllipsis>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.identity ?? ''}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>
                        </ContainerColumn>
                    </Option>
                ))}
            </>
            :
            <>
                {userController.tableUsers.data.map(user => (
                    <Option key={user.id} value={user.id} label={user.name} data={user}>
                        <ContainerColumn>
                            <ContentColumn width={itemWidth}>
                                <AvatarImage
                                    useCursorPointer={!!user.urlImageProfile}
                                    title={user.name}
                                    size={25}
                                    url={user.urlImageProfile ?? ''}
                                    round={true}
                                    withBorder={true}
                                    hasPreviewImage={false}
                                />
                                <TextMainEllipsis style={{marginLeft: 5}}>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.name}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>
                            <ContentColumn width={itemWidth}>
                                <TextMainEllipsis>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.telephone ?? ''}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>

                            <ContentColumn width={itemWidth}>
                                <TextMainEllipsis>
                                    <HighLightWords
                                        searchWords={userController.searchParam}
                                        textToHighlight={user.client?.name ?? ''}
                                        fontSize={13}
                                    />
                                </TextMainEllipsis>
                            </ContentColumn>
                        </ContainerColumn>
                    </Option>
                ))}
            </>;

    const optionsHeader =
        props.role === UserRole.attendant ?
            <ContainerHeaderColumn>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Nome</ContainerHeaderText>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Email</ContainerHeaderText>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Identificação</ContainerHeaderText>
            </ContainerHeaderColumn>
            :
            <ContainerHeaderColumn>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Nome</ContainerHeaderText>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Telefone</ContainerHeaderText>
                <ContainerHeaderText color={colors.hintInverse} width={itemWidth}>Empresa</ContainerHeaderText>
            </ContainerHeaderColumn>;


    return (
        <SelectSearchRemote<UserEntity>
            onChange={(filter, user) => {
                props.handleSelectUser(filter, user);
            }}
            onClickRemoveFilter={() => {
                userController.tableUsers.page = 0;
            }}
            required={props.required}
            disabled={props.disabled}
            onSearch={(value) => onSearch(value)}
            size={props.size}
            useRemoveFilter={props.useRemoveFilter}
            width={props.width}
            widthPopup={props.widthPopup ?? 632}
            label={props.label}
            placeHolder={props.placeHolder}
            value={props.value}
            loading={userController.loading}
            options={options}
            optionsLength={userController.tableUsers.data.length}
            search={true}
            searchValue={userController.searchParam}
            hasMore={userController.tableUsers.hasMore}
            loadMore={() => userController.loadMoreDataFromPage(false, userController.role, props.idClient, props.idSector)}
            messageError={props.messageError}
            iconLeftName={props.iconLeftName}
            borderColor={props.borderColor}
            dropdownRender={menu => (
                <>
                    {optionsHeader}
                    {menu}
                </>
            )}
        />
    )
});
export default SelectSearchUser;
